import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { api } from "../api";
import { DataTable } from "../components/DataTable";
import type { Supplier } from "../types";

export function SupplierList() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["suppliers", search],
    queryFn: () => api.listSuppliers({ search: search || undefined, per_page: 200 }),
  });

  const createMutation = useMutation({
    mutationFn: (d: Partial<Supplier>) => api.createSupplier(d),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      setShowForm(false);
    },
  });

  const columns = [
    { key: "name", label: "供應商名稱", sortable: true, filterable: true, render: (s: Supplier) => (
      <span className="font-medium text-slate-900">{s.name}</span>
    )},
    { key: "contact", label: "聯絡資訊", sortable: true, render: (s: Supplier) => (
      <span className="text-xs text-slate-500">{s.contact ?? "-"}</span>
    )},
    { key: "certification", label: "認證", sortable: true, filterable: true, render: (s: Supplier) => (
      <span className="text-xs">{s.certification ?? "-"}</span>
    )},
  ];

  return (
    <div className="max-w-5xl space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">供應商</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          <Plus size={16} /> 新增
        </button>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          className="input pl-9"
          placeholder="搜尋供應商..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {showForm && (
        <form
          className="rounded-xl border border-slate-200 bg-white p-5 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            createMutation.mutate({
              name: fd.get("name") as string,
              contact: (fd.get("contact") as string) || undefined,
              certification: (fd.get("certification") as string) || undefined,
            });
          }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <input name="name" className="input" placeholder="供應商名稱 *" required />
            <input name="contact" className="input" placeholder="聯絡資訊" />
            <input name="certification" className="input" placeholder="認證" />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={createMutation.isPending}>
              儲存
            </button>
            <button type="button" className="btn" onClick={() => setShowForm(false)}>
              取消
            </button>
          </div>
        </form>
      )}

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        exportable
        exportFilename="供應商列表"
        emptyMessage="沒有供應商資料"
      />
    </div>
  );
}
